import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    Search, Download, Plus, ExternalLink, CheckCircle2, Globe,
    Mail, Building2, AlertCircle, Loader, ChevronDown, ChevronRight,
    Users, AtSign, Database, Info, Star, Bot, Calendar, Send,
    Eye, CheckSquare, Square, Check, X,
} from "lucide-react";
import api from "../../utils/api";

// ─── Email preview utilities (mirrors EmailCenter logic) ──────────────────────
const EMAIL_BRANDS = {
    tinyglobal: { primaryColor:"#071d2e", accentColor:"#c9a227", label:"TinyGlobal", footer:"© 2026 TinyGlobal Pty Ltd · Sydney, Australia" },
    yadata:     { primaryColor:"#1a237e", accentColor:"#ffd600", label:"Yadata",     footer:"© 2026 Yadata Pty Ltd · Sydney, Australia" },
};

function mergeVars(str = "", vars = {}) {
    return str.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] !== undefined ? vars[k] : "");
}

function buildProspectPreview(tpl, vars = {}) {
    const cfg = EMAIL_BRANDS[tpl.brand] || EMAIL_BRANDS.tinyglobal;
    const primary = tpl.primaryColor || cfg.primaryColor;
    const accent  = tpl.accentColor  || cfg.accentColor;
    const logo = tpl.logoUrl
        ? `<img src="${tpl.logoUrl}" alt="" style="height:36px;display:block;">`
        : tpl.brand === "yadata"
          ? `<span style="color:#fff;font-size:20px;font-weight:800;">Ya<span style="color:${accent}">data</span></span>`
          : `<span style="color:#fff;font-size:20px;font-weight:800;">Tiny<span style="color:${accent}">Global</span></span>`;
    const body = mergeVars(tpl.bodyContent || "", vars).split("\n")
        .map(l => l.trim() ? `<p style="margin:0 0 12px;color:#334155;font-size:14px;line-height:1.7;">${l}</p>` : "").join("");
    const btn = tpl.buttonText
        ? `<div style="margin:20px 0;"><a href="#" style="display:inline-block;padding:11px 24px;background:${primary};color:#fff;text-decoration:none;border-radius:8px;font-weight:600;font-size:13px;">${mergeVars(tpl.buttonText, vars)}</a></div>` : "";
    return `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f8fafc;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
<div style="max-width:560px;margin:16px auto;background:#fff;border-radius:10px;overflow:hidden;border:1px solid #e2e8f0;">
  <div style="background:${primary};padding:20px 24px;">${logo}</div>
  <div style="padding:24px;">
    ${tpl.headerTitle ? `<h2 style="margin:0 0 14px;color:#0f172a;font-size:18px;font-weight:700;">${mergeVars(tpl.headerTitle, vars)}</h2>` : ""}
    ${body}${btn}
  </div>
  <div style="background:#f1f5f9;padding:14px 24px;text-align:center;font-size:11px;color:#94a3b8;">${tpl.footerText || cfg.footer}</div>
</div></body></html>`;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const AU_STATES = [
    { value:"",    label:"All States" },
    { value:"NSW", label:"NSW — New South Wales" },
    { value:"VIC", label:"VIC — Victoria" },
    { value:"QLD", label:"QLD — Queensland" },
    { value:"WA",  label:"WA — Western Australia" },
    { value:"SA",  label:"SA — South Australia" },
    { value:"TAS", label:"TAS — Tasmania" },
    { value:"ACT", label:"ACT — Australian Capital Territory" },
    { value:"NT",  label:"NT — Northern Territory" },
];

const INDUSTRIES = [
    { value:"",               label:"All Industries" },
    { value:"import export",  label:"Import / Export" },
    { value:"wholesale",      label:"Wholesale Trade" },
    { value:"freight",        label:"Freight & Logistics" },
    { value:"manufacturing",  label:"Manufacturing" },
    { value:"food beverage",  label:"Food & Beverage" },
    { value:"retail",         label:"Retail Trade" },
    { value:"mining",         label:"Mining & Resources" },
    { value:"agriculture",    label:"Agriculture" },
    { value:"construction",   label:"Construction" },
    { value:"pharma",         label:"Pharmaceuticals" },
    { value:"automotive",     label:"Automotive" },
    { value:"electronics",    label:"Electronics" },
];

const LIMITS = [
    { value:10,  label:"10 results" },
    { value:25,  label:"25 results" },
    { value:50,  label:"50 results" },
    { value:100, label:"100 results" },
];

const STATUS_OPTIONS = [
    { value:"new",       label:"New",       color:"#6B7280", bg:"#6B728020" },
    { value:"contacted", label:"Contacted", color:"#F59E0B", bg:"#F59E0B20" },
    { value:"meeting",   label:"Meeting",   color:"#10B981", bg:"#10B98120" },
    { value:"proposal",  label:"Proposal",  color:"#8B5CF6", bg:"#8B5CF620" },
    { value:"customer",  label:"Customer",  color:"#3B82F6", bg:"#3B82F620" },
    { value:"lost",      label:"Lost",      color:"#EF4444", bg:"#EF444420" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function calcScore(r) {
    let s = 0;
    if (r.abnStatus === "active") s += 30;
    if (r.website)                s += 25;
    if (r.email)                  s += 25;
    if (r.entityType?.toLowerCase().includes("private")) s += 15;
    if (r.enriched)               s += 5;
    return Math.min(s, 100);
}

function getAIInsights(r) {
    const pros = [], cons = [];
    if (r.abnStatus === "active")               pros.push("Active ABN — currently trading");
    else                                         cons.push("Inactive ABN — verify business status");
    if (r.website)                               pros.push("Active web presence detected");
    else                                         cons.push("No website — harder to research");
    if (r.email)                                 pros.push("Direct contact email available");
    else                                         cons.push("No email — may need cold outreach");
    if (r.entityType?.includes("Private"))       pros.push("Private company — decision maker accessible");
    if (r.entityType?.includes("Public"))        pros.push("Public company — larger deal potential");
    pros.push("Potential fit for TinyGlobal logistics platform");
    return { pros, cons };
}

function fmtDate(d) {
    if (!d) return null;
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day:"numeric", month:"short", year:"2-digit" });
}

function abnKey(r) { return (r.abn || "").replace(/\s/g, "") || r.id; }

function exportCSV(rows) {
    const header = ["Name","ABN","State","Entity","Website","Email","ABN Status","Score","Status","Follow-Up","In CRM"];
    const csv = [header, ...rows.map(r => [
        `"${(r.name||"").replace(/"/g,'""')}"`, r.abn||"", r.state||"", r.entityType||"",
        r.website||"", r.email||"", r.abnStatus||"", r.score||"",
        r.status||"", r.followUp||"", r.inCRM?"Yes":"No",
    ].join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = `sme-prospects-${Date.now()}.csv`;
    a.click();
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, sub }) {
    return (
        <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor:`${color}22` }}>
                <Icon size={18} style={{ color }}/>
            </div>
            <div>
                <p className="text-2xl font-bold text-white leading-none">{value}</p>
                <p className="text-xs text-slate-400 mt-1">{label}</p>
                {sub && <p className="text-[10px] text-slate-600 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

function ABN({ value }) {
    if (!value) return <span className="text-slate-600">—</span>;
    const d = value.replace(/\D/g, "");
    const fmt = d.length === 11 ? `${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}` : value;
    return <span className="font-mono text-[11px] text-slate-400">{fmt}</span>;
}

function ScoreBadge({ score }) {
    const stars = Math.round(score / 20);
    let label, color;
    if (score >= 90)      { label="HOT";  color="#EF4444"; }
    else if (score >= 70) { label="HIGH"; color="#F97316"; }
    else if (score >= 50) { label="MED";  color="#F59E0B"; }
    else                  { label="LOW";  color="#6B7280"; }
    return (
        <div className="flex flex-col items-center gap-0.5 min-w-[38px]">
            <span className="text-[10px] font-bold tabular-nums" style={{ color }}>{score}%</span>
            <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                    <Star key={i} size={8} fill={i <= stars ? color : "transparent"} stroke={color} strokeWidth={1.5}/>
                ))}
            </div>
            <span className="text-[8px] font-bold tracking-widest" style={{ color }}>{label}</span>
        </div>
    );
}

function StatusPill({ status, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
                className="flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold border transition-all whitespace-nowrap"
                style={{ color: opt.color, borderColor: opt.color + "60", backgroundColor: opt.bg }}>
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: opt.color }}/>
                {opt.label}
                <ChevronDown size={8}/>
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-1 bg-[#1F2937] border border-[#374151] rounded-lg shadow-2xl z-50 py-1 min-w-[120px]">
                    {STATUS_OPTIONS.map(s => (
                        <button key={s.value} onClick={(e) => { e.stopPropagation(); onChange(s.value); setOpen(false); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-[#374151] transition-colors"
                            style={{ color: s.color }}>
                            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: s.color }}/>
                            {s.label}
                            {s.value === status && <Check size={9} className="ml-auto"/>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function BulkBar({ count, onCRM, onExport, onContacted, onClear }) {
    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-[#1A2332] border border-[#374151] rounded-2xl shadow-2xl">
            <span className="text-sm font-bold text-white mr-1">{count} selected</span>
            <div className="w-px h-5 bg-[#374151] mx-1"/>
            <button onClick={onCRM}       className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30 transition-colors">
                <Database size={11}/> Add to CRM
            </button>
            <button onClick={onExport}    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 transition-colors">
                <Download size={11}/> Export
            </button>
            <button onClick={onContacted} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600/20 border border-amber-500/30 text-amber-400 hover:bg-amber-600/30 transition-colors">
                <Check size={11}/> Mark Contacted
            </button>
            <button onClick={onClear} className="p-1.5 text-slate-500 hover:text-white transition-colors ml-1">
                <X size={14}/>
            </button>
        </div>
    );
}

function ExpandedRow({ r, note, onNoteChange, status, onStatusChange, followUp, onFollowUpChange, emailLog, onLogEmail, score }) {
    const { pros, cons } = getAIInsights(r);
    return (
        <tr>
            <td colSpan={7} className="p-0">
                <div className="bg-[#060E1A] border-b border-[#1F2937] px-8 py-6">
                    <div className="grid grid-cols-3 gap-8">

                        {/* ── Left: Details + Pipeline + Follow-up ── */}
                        <div className="space-y-5">
                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Company Details</p>
                                <dl className="space-y-2">
                                    {[
                                        ["ABN",    <ABN value={r.abn}/>],
                                        ["Entity", <span className="text-slate-300 text-xs">{r.entityType || "—"}</span>],
                                        ["State",  <span className="text-slate-300 text-xs">{r.state || "—"}</span>],
                                        ["Web",    r.website
                                            ? <a href={r.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-xs flex items-center gap-1">
                                                {r.website.replace(/^https?:\/\/(www\.)?/,"").replace(/\/$/,"")}
                                                <ExternalLink size={9}/>
                                              </a>
                                            : <span className="text-slate-600 text-xs">—</span>],
                                        ["Email",  r.email
                                            ? <a href={`mailto:${r.email}`} className="text-green-400 hover:underline text-xs">{r.email}</a>
                                            : <span className="text-slate-600 text-xs">—</span>],
                                    ].map(([k, v]) => (
                                        <div key={k} className="flex items-start gap-3">
                                            <dt className="text-slate-500 text-[11px] w-12 shrink-0 pt-0.5">{k}</dt>
                                            <dd>{v}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>

                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Pipeline Status</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {STATUS_OPTIONS.map(s => (
                                        <button key={s.value} onClick={() => onStatusChange(s.value)}
                                            className="px-2 py-1 rounded text-[10px] font-bold border transition-all"
                                            style={status === s.value
                                                ? { color:s.color, borderColor:s.color, backgroundColor:s.bg }
                                                : { color:"#475569", borderColor:"#334155" }}>
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Next Follow-Up</p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex items-center gap-1.5">
                                        <Calendar size={12} className="text-slate-500"/>
                                        <input type="date" value={followUp || ""} onChange={e => onFollowUpChange(e.target.value)}
                                            className="bg-[#1F2937] border border-[#374151] rounded-lg px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-blue-500"/>
                                    </div>
                                    {followUp && (
                                        <span className="text-[11px] text-blue-400 font-semibold">📅 {fmtDate(followUp)}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Centre: AI Analysis ── */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                    <Bot size={11} className="text-purple-400"/> AI Analysis
                                </p>
                                <ScoreBadge score={score}/>
                            </div>

                            <div className="space-y-2">
                                {pros.map((p, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs text-green-400">
                                        <CheckCircle2 size={11} className="shrink-0 mt-0.5"/> {p}
                                    </div>
                                ))}
                                {cons.map((c, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs text-amber-400">
                                        <AlertCircle size={11} className="shrink-0 mt-0.5"/> {c}
                                    </div>
                                ))}
                            </div>

                            <div className="bg-[#111827] rounded-xl p-4 border border-[#1F2937]">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Revenue Estimate</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-[#1F2937] rounded-lg p-2.5">
                                        <p className="text-base font-bold text-white">$3k–$12k</p>
                                        <p className="text-[10px] text-slate-500 mt-0.5">Implementation</p>
                                    </div>
                                    <div className="bg-[#1F2937] rounded-lg p-2.5">
                                        <p className="text-base font-bold text-white">$300–$900<span className="text-[10px] font-normal text-slate-500">/mo</span></p>
                                        <p className="text-[10px] text-slate-500 mt-0.5">Recurring</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Right: Notes + Email Log ── */}
                        <div className="space-y-4">
                            <div>
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Notes</p>
                                <textarea value={note || ""} onChange={e => onNoteChange(e.target.value)}
                                    placeholder="Called ops manager — interested in Control Tower, follow up August…"
                                    rows={5}
                                    className="w-full bg-[#1F2937] border border-[#374151] rounded-xl px-3 py-2.5 text-xs text-slate-300 placeholder-slate-600 focus:outline-none focus:border-blue-500 resize-none transition-colors"/>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Email History</p>
                                </div>
                                {emailLog?.length > 0 ? (
                                    <div className="space-y-1 mb-2">
                                        {emailLog.map((entry, i) => (
                                            <div key={i} className="flex items-center gap-2 text-[11px]">
                                                <CheckCircle2 size={9} className="text-green-400 shrink-0"/>
                                                <span className="text-slate-300">{entry.label}</span>
                                                <span className="ml-auto text-slate-600">{entry.date}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-slate-600 mb-2">No emails logged yet.</p>
                                )}
                                <div className="flex flex-wrap gap-1.5">
                                    {["Intro email", "Follow-up", "Proposal sent", "Demo booked"].map(label => (
                                        <button key={label} onClick={() => onLogEmail(label)}
                                            className="flex items-center gap-1 px-2 py-1 bg-[#1F2937] border border-[#374151] rounded text-[10px] text-slate-400 hover:text-white hover:border-slate-500 transition-colors">
                                            <Send size={8}/> {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </td>
        </tr>
    );
}

// ─── Prospect Email Modal ─────────────────────────────────────────────────────
function ProspectEmailModal({ prospect, onClose, onSent }) {
    const [templates,      setTemplates]      = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [selectedTpl,    setSelectedTpl]    = useState(null);
    const [recipientName,  setRecipientName]  = useState("");
    const [note,           setNote]           = useState("");
    const [sending,        setSending]        = useState(false);
    const [sent,           setSent]           = useState(false);
    const [sendError,      setSendError]      = useState(null);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [r1, r2] = await Promise.allSettled([
                    api.get("/api/email/templates?brand=tinyglobal"),
                    api.get("/api/email/templates?brand=yadata"),
                ]);
                const t1 = r1.status === "fulfilled" ? (r1.value.data.templates || []) : [];
                const t2 = r2.status === "fulfilled" ? (r2.value.data.templates || []) : [];
                const all = [...t1, ...t2].filter(t => t.status === "active");
                setTemplates(all);
                if (all.length > 0) setSelectedTpl(all[0]);
            } catch (_) {}
            setLoading(false);
        };
        fetchAll();
    }, []);

    const previewVars = useMemo(() => ({
        recipientName: recipientName || prospect.name,
        companyName:   prospect.name,
        note:          note || "We'd love to connect and show you how TinyGlobal can help streamline your logistics.",
    }), [recipientName, prospect.name, note]);

    const previewHtml = useMemo(() =>
        selectedTpl ? buildProspectPreview(selectedTpl, previewVars) : "",
    [selectedTpl, previewVars]);

    const handleSend = async () => {
        if (!selectedTpl || !prospect.email) return;
        setSending(true); setSendError(null);
        try {
            await api.post("/api/email/send", {
                templateId: selectedTpl.id,
                to: prospect.email,
                vars: previewVars,
            });
            setSent(true);
            onSent(selectedTpl.name);
        } catch (e) {
            setSendError(e.response?.data?.error || "Send failed — check Email Center is configured.");
        } finally { setSending(false); }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background:"rgba(0,0,0,0.82)" }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="bg-[#0D1520] border border-[#1F2937] rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col" style={{ maxHeight:"90vh" }}>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#1F2937] shrink-0">
                    <div>
                        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
                            <Mail size={15} className="text-blue-400"/>
                            Send Email — {prospect.name}
                        </h3>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                            To: <span className="text-green-400 font-semibold">{prospect.email || "No email available"}</span>
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-500 hover:text-white hover:bg-[#1F2937] rounded-lg transition-colors">
                        <X size={15}/>
                    </button>
                </div>

                {/* Success state */}
                {sent ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                            <CheckCircle2 size={32} className="text-green-400"/>
                        </div>
                        <div className="text-center">
                            <p className="text-white font-bold text-lg">Email Sent!</p>
                            <p className="text-slate-400 text-sm mt-1">
                                <span className="text-white font-semibold">{selectedTpl?.name}</span> sent to {prospect.email}
                            </p>
                        </div>
                        <button onClick={onClose} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-semibold transition-colors">
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-1 overflow-hidden" style={{ minHeight:0 }}>

                        {/* Left: template list */}
                        <div className="w-52 border-r border-[#1F2937] flex flex-col shrink-0 overflow-hidden">
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 py-3 border-b border-[#1F2937]">Templates</p>
                            {loading ? (
                                <div className="flex justify-center py-10"><Loader size={18} className="animate-spin text-slate-600"/></div>
                            ) : templates.length === 0 ? (
                                <div className="px-4 py-5 text-[11px] text-slate-500 leading-relaxed">
                                    No active templates found.<br/>
                                    Go to <strong className="text-blue-400">Email Center</strong> to create templates first.
                                </div>
                            ) : (
                                <div className="flex-1 overflow-y-auto divide-y divide-[#1F2937]">
                                    {templates.map(t => (
                                        <button key={t.id} onClick={() => setSelectedTpl(t)}
                                            className={`w-full text-left px-4 py-3 transition-colors hover:bg-[#1F2937]/50 ${selectedTpl?.id === t.id ? "bg-blue-900/20 border-l-2 border-blue-500" : ""}`}>
                                            <p className={`text-xs font-bold truncate ${selectedTpl?.id === t.id ? "text-white" : "text-slate-300"}`}>{t.name}</p>
                                            <p className="text-[10px] text-slate-500 truncate mt-0.5">{t.subject}</p>
                                            <span className="text-[9px] font-bold text-slate-600 uppercase mt-1 inline-block">
                                                {EMAIL_BRANDS[t.brand]?.label || t.brand}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right: preview + send form */}
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* Live preview */}
                            <div className="flex-1 overflow-auto bg-[#060E1A] p-3">
                                {selectedTpl ? (
                                    <iframe
                                        key={selectedTpl.id + note + recipientName}
                                        title="email-preview"
                                        srcDoc={previewHtml}
                                        className="w-full border-0 rounded-xl shadow-lg"
                                        style={{ height:"320px" }}
                                        sandbox="allow-same-origin"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-48 text-slate-600 text-sm">
                                        Select a template to preview
                                    </div>
                                )}
                            </div>

                            {/* Send form */}
                            <div className="px-5 py-4 border-t border-[#1F2937] space-y-3 shrink-0">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Recipient Name</label>
                                        <input type="text" value={recipientName}
                                            onChange={e => setRecipientName(e.target.value)}
                                            placeholder={prospect.name}
                                            className="w-full px-3 py-2 bg-[#1F2937] border border-[#374151] rounded-lg text-xs text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"/>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Sending To</label>
                                        <input readOnly value={prospect.email || "No email available"}
                                            className="w-full px-3 py-2 bg-[#111827] border border-[#1F2937] rounded-lg text-xs text-slate-400 cursor-not-allowed"/>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        Custom Note <span className="font-normal text-slate-600">(replaces {"{{note}}"} in template)</span>
                                    </label>
                                    <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                                        placeholder="e.g. We noticed you import regularly and thought TinyGlobal could help streamline your logistics…"
                                        className="w-full px-3 py-2 bg-[#1F2937] border border-[#374151] rounded-lg text-xs text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 resize-none transition-colors"/>
                                </div>
                                {sendError && (
                                    <div className="flex items-center gap-2 text-xs text-red-400">
                                        <AlertCircle size={11}/> {sendError}
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] text-slate-600">
                                        Template: <span className="text-slate-400">{selectedTpl?.name || "—"}</span>
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <button onClick={onClose} className="text-xs text-slate-400 hover:text-white transition-colors px-3 py-2">
                                            Cancel
                                        </button>
                                        <button onClick={handleSend}
                                            disabled={sending || !selectedTpl || !prospect.email}
                                            className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors">
                                            {sending ? <Loader size={12} className="animate-spin"/> : <Send size={12}/>}
                                            {sending ? "Sending…" : "Send Email"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SMEProspector() {
    const [query, setQuery]         = useState("");
    const [state, setState]         = useState("");
    const [industry, setIndustry]   = useState("");
    const [limit, setLimit]         = useState(25);

    const [jobId, setJobId]         = useState(null);
    const [jobData, setJobData]     = useState(null);
    const [searching, setSearching] = useState(false);
    const [error, setError]         = useState(null);

    const [filterText, setFilterText] = useState("");
    const [sortCol, setSortCol]     = useState("score");
    const [sortDir, setSortDir]     = useState("desc");
    const [addingCRM, setAddingCRM] = useState(null);

    // Phase 1 state
    const [selected,     setSelected]     = useState(new Set());
    const [expandedId,   setExpandedId]   = useState(null);
    const [emailTarget,  setEmailTarget]  = useState(null);

    // Persisted per ABN key
    const load = (k, d = "{}") => { try { return JSON.parse(localStorage.getItem(k) || d); } catch { return JSON.parse(d); } };
    const [notes,     setNotes]     = useState(() => load("sme_notes"));
    const [statuses,  setStatuses]  = useState(() => load("sme_statuses"));
    const [followUps, setFollowUps] = useState(() => load("sme_followups"));
    const [emailLogs, setEmailLogs] = useState(() => load("sme_emaillogs"));

    const pollRef = useRef(null);

    useEffect(() => { localStorage.setItem("sme_notes",     JSON.stringify(notes));     }, [notes]);
    useEffect(() => { localStorage.setItem("sme_statuses",  JSON.stringify(statuses));  }, [statuses]);
    useEffect(() => { localStorage.setItem("sme_followups", JSON.stringify(followUps)); }, [followUps]);
    useEffect(() => { localStorage.setItem("sme_emaillogs", JSON.stringify(emailLogs)); }, [emailLogs]);

    const startPolling = useCallback((id) => {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const res = await api.get(`/api/prospector/results/${id}`);
                setJobData(res.data);
                if (res.data.status !== "running") { clearInterval(pollRef.current); setSearching(false); }
            } catch (e) {
                clearInterval(pollRef.current); setSearching(false);
                setError(e.response?.data?.error || e.message);
            }
        }, 2000);
    }, []);

    useEffect(() => () => clearInterval(pollRef.current), []);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        setError(null); setJobData(null); setJobId(null);
        setSearching(true); setFilterText(""); setSelected(new Set()); setExpandedId(null);
        try {
            const q = industry ? `${query.trim()} ${industry}` : query.trim();
            const res = await api.post("/api/prospector/search", { query: q, state, industry, limit });
            setJobId(res.data.jobId);
            startPolling(res.data.jobId);
        } catch (e) {
            setSearching(false);
            setError(e.response?.data?.error || e.message);
        }
    };

    const handleAddCRM = async (prospect) => {
        if (!jobId || prospect.inCRM) return;
        setAddingCRM(prospect.id);
        try {
            await api.post("/api/prospector/add-crm", { jobId, prospectId: prospect.id });
            setJobData(prev => prev ? {
                ...prev,
                stats: { ...prev.stats, crm: (prev.stats.crm || 0) + 1 },
                results: prev.results.map(r => r.id === prospect.id ? { ...r, inCRM: true } : r),
            } : prev);
        } catch (e) { setError(e.response?.data?.error || e.message); }
        finally { setAddingCRM(null); }
    };

    // Handlers using ABN as stable key
    const updateNote     = (r, v) => setNotes(p     => ({ ...p, [abnKey(r)]: v }));
    const updateStatus   = (r, v) => setStatuses(p  => ({ ...p, [abnKey(r)]: v }));
    const updateFollowUp = (r, v) => setFollowUps(p => ({ ...p, [abnKey(r)]: v }));
    const logEmail       = (r, label) => {
        const entry = { label, date: new Date().toLocaleDateString("en-AU", { day:"numeric", month:"short" }) };
        setEmailLogs(p => ({ ...p, [abnKey(r)]: [...(p[abnKey(r)] || []), entry] }));
    };

    // Derived
    const results = jobData?.results || [];
    const stats   = jobData?.stats   || { found:0, websites:0, emails:0, crm:0 };

    const enriched = results.map(r => ({
        ...r,
        score:    calcScore(r),
        status:   statuses[abnKey(r)]  || "new",
        note:     notes[abnKey(r)]     || "",
        followUp: followUps[abnKey(r)] || "",
        emailLog: emailLogs[abnKey(r)] || [],
    }));

    const filtered = enriched
        .filter(r => !filterText
            || r.name?.toLowerCase().includes(filterText.toLowerCase())
            || r.email?.toLowerCase().includes(filterText.toLowerCase())
            || r.state?.toLowerCase().includes(filterText.toLowerCase()))
        .sort((a, b) => {
            if (sortCol === "score") return sortDir === "desc" ? b.score - a.score : a.score - b.score;
            const va = (a[sortCol] || "").toString().toLowerCase();
            const vb = (b[sortCol] || "").toString().toLowerCase();
            return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        });

    const toggleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortCol(col); setSortDir(col === "score" ? "desc" : "asc"); }
    };
    const SortIcon = ({ col }) => sortCol === col
        ? <span className="ml-1 text-blue-400">{sortDir === "asc" ? "↑" : "↓"}</span>
        : <span className="ml-1 text-slate-700">↕</span>;

    const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
    const toggleSelectAll = () => allSelected
        ? setSelected(new Set())
        : setSelected(new Set(filtered.map(r => r.id)));
    const toggleSelect = (id) => setSelected(prev => {
        const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });

    const handleBulkCRM = async () => {
        for (const id of selected) {
            const p = filtered.find(r => r.id === id);
            if (p && !p.inCRM) await handleAddCRM(p);
        }
        setSelected(new Set());
    };
    const handleBulkContacted = () => {
        filtered.filter(r => selected.has(r.id)).forEach(r => updateStatus(r, "contacted"));
        setSelected(new Set());
    };
    const handleBulkExport = () => exportCSV(filtered.filter(r => selected.has(r.id)));

    const isDone    = jobData?.status === "done";
    const isRunning = jobData?.status === "running" || searching;
    const progress  = jobData?.progress || 0;
    const phase     = jobData?.phase    || "Starting…";

    const HEADERS = [
        { key:"name",  label:"Company" },
        { key:"score", label:"Score" },
        { key:"state", label:"State" },
        { key:null,    label:"Status" },
        { key:null,    label:"Web / Email" },
        { key:null,    label:"Actions" },
    ];

    return (
        <div className="min-h-screen bg-[#0B1120] text-white font-sans p-5 space-y-5 pb-28">

            {/* ── Header ── */}
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Users size={22} className="text-blue-400"/> SME Prospector
                    </h1>
                    <p className="text-sm text-slate-400 mt-0.5">
                        Search ABR · Enrich websites & emails · Score leads · Track pipeline
                    </p>
                </div>
                {isDone && filtered.length > 0 && (
                    <button onClick={() => exportCSV(filtered)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600/20 border border-green-500/40 rounded-lg text-sm text-green-400 hover:bg-green-600/30 transition-colors">
                        <Download size={14}/> Export CSV
                    </button>
                )}
            </div>

            {/* ── Info banner ── */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl px-4 py-3 flex items-start gap-3">
                <Info size={16} className="text-blue-400 shrink-0 mt-0.5"/>
                <p className="text-[12px] text-blue-300 leading-relaxed">
                    <strong>How it works:</strong> Searches the ABR by keyword and state, finds each company's website, extracts contact emails, and scores lead quality.
                    Without <code className="bg-blue-900/40 px-1 rounded">ABR_GUID</code>, demo results are shown.
                    Notes, status, and follow-up dates persist across searches.
                </p>
            </div>

            {/* ── Search form ── */}
            <form onSubmit={handleSearch} className="bg-[#111827] border border-[#1F2937] rounded-xl p-5">
                <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px] relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
                        <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="e.g. timber importer, seafood exporter, battery distributor…"
                            className="w-full pl-9 pr-4 py-2.5 bg-[#1F2937] border border-[#374151] rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors"/>
                    </div>
                    {[
                        [AU_STATES,  state,    setState],
                        [INDUSTRIES, industry, setIndustry],
                        [LIMITS,     limit,    v => setLimit(Number(v))],
                    ].map(([opts, val, setter], i) => (
                        <div key={i} className="relative">
                            <select value={val} onChange={e => setter(e.target.value)}
                                className="appearance-none pl-3 pr-8 py-2.5 bg-[#1F2937] border border-[#374151] rounded-lg text-sm text-slate-300 focus:outline-none focus:border-blue-500 cursor-pointer">
                                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
                        </div>
                    ))}
                    <button type="submit" disabled={isRunning || !query.trim()}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-semibold text-white transition-colors">
                        {isRunning ? <><Loader size={14} className="animate-spin"/> Searching…</> : <><Search size={14}/> Search ABR</>}
                    </button>
                </div>
            </form>

            {error && (
                <div className="bg-red-900/30 border border-red-800 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle size={15}/> {error}
                </div>
            )}

            {/* ── Progress bar ── */}
            {(isRunning || isDone) && (
                <div className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300 flex items-center gap-2">
                            {isRunning && <Loader size={13} className="animate-spin text-blue-400"/>}
                            {isDone    && <CheckCircle2 size={13} className="text-green-400"/>}
                            {phase}
                        </span>
                        <span className="text-slate-500 tabular-nums">{progress}%</span>
                    </div>
                    <div className="w-full bg-[#1F2937] rounded-full h-2 overflow-hidden">
                        <div className="h-2 rounded-full transition-all duration-500"
                            style={{ width:`${progress}%`, background: isDone ? "#10B981" : "linear-gradient(90deg,#3B82F6,#8B5CF6)" }}/>
                    </div>
                </div>
            )}

            {/* ── Stat cards ── */}
            {(isRunning || isDone) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard icon={Building2} label="Found"        value={stats.found}    color="#3B82F6" sub="from ABR search"/>
                    <StatCard icon={Globe}     label="Websites"     value={stats.websites} color="#8B5CF6" sub={`${Math.round((stats.websites/Math.max(stats.found,1))*100)}% hit rate`}/>
                    <StatCard icon={AtSign}    label="Emails"       value={stats.emails}   color="#10B981" sub={`${Math.round((stats.emails/Math.max(stats.found,1))*100)}% of leads`}/>
                    <StatCard icon={Database}  label="Added to CRM" value={stats.crm}      color="#F59E0B" sub="select + bulk add"/>
                </div>
            )}

            {/* ── Results table ── */}
            {results.length > 0 && (
                <div className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
                    {/* Table toolbar */}
                    <div className="flex items-center justify-between px-5 py-4 gap-3 flex-wrap border-b border-[#1F2937]">
                        <p className="text-sm font-bold text-white">
                            {filtered.length} of {results.length} leads
                            {selected.size > 0 && <span className="ml-2 text-[12px] text-blue-400 font-normal">· {selected.size} selected</span>}
                            {!isDone && <span className="ml-2 text-[11px] text-blue-400 font-normal animate-pulse">enriching…</span>}
                        </p>
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600"/>
                                <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)}
                                    placeholder="Filter…"
                                    className="pl-8 pr-3 py-1.5 bg-[#1F2937] border border-[#374151] rounded-lg text-xs text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 w-36"/>
                            </div>
                            {isDone && (
                                <button onClick={() => exportCSV(filtered)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 border border-green-500/30 rounded-lg text-xs text-green-400 hover:bg-green-600/30 transition-colors">
                                    <Download size={11}/> CSV
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead>
                                <tr className="bg-[#0D1520]">
                                    {/* Select all */}
                                    <th className="px-4 py-2.5 w-8">
                                        <button onClick={toggleSelectAll}>
                                            {allSelected
                                                ? <CheckSquare size={14} className="text-blue-400"/>
                                                : <Square size={14} className="text-slate-600"/>}
                                        </button>
                                    </th>
                                    {HEADERS.map(h => (
                                        <th key={h.label}
                                            onClick={() => h.key && toggleSort(h.key)}
                                            className={`px-4 py-2.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap ${h.key ? "cursor-pointer hover:text-slate-300 select-none" : ""}`}>
                                            {h.label}{h.key && <SortIcon col={h.key}/>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#1F2937]">
                                {filtered.map(r => (
                                    <React.Fragment key={r.id}>
                                        {/* ── Main row ── */}
                                        <tr className={`group transition-colors hover:bg-[#1F2937]/50 ${!r.enriched ? "opacity-60" : ""} ${selected.has(r.id) ? "bg-blue-900/10" : ""}`}>

                                            {/* Checkbox */}
                                            <td className="px-4 py-3 w-8" onClick={e => e.stopPropagation()}>
                                                <button onClick={() => toggleSelect(r.id)}>
                                                    {selected.has(r.id)
                                                        ? <CheckSquare size={14} className="text-blue-400"/>
                                                        : <Square size={14} className="text-slate-600 group-hover:text-slate-400"/>}
                                                </button>
                                            </td>

                                            {/* Company name + ABN */}
                                            <td className="px-4 py-3 cursor-pointer"
                                                onClick={() => setExpandedId(prev => prev === r.id ? null : r.id)}>
                                                <div className="flex items-center gap-2">
                                                    <ChevronRight size={12} className={`text-slate-600 shrink-0 transition-transform duration-200 ${expandedId === r.id ? "rotate-90 text-blue-400" : ""}`}/>
                                                    {!r.enriched
                                                        ? <Loader size={11} className="animate-spin text-slate-600 shrink-0"/>
                                                        : <Building2 size={11} className="text-slate-600 shrink-0"/>}
                                                    <div>
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-semibold text-slate-200 max-w-[200px] truncate" title={r.name}>{r.name}</span>
                                                            {r._demo && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1 rounded border border-amber-500/30 shrink-0">demo</span>}
                                                        </div>
                                                        <ABN value={r.abn}/>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Score */}
                                            <td className="px-4 py-3">
                                                <ScoreBadge score={r.score}/>
                                            </td>

                                            {/* State */}
                                            <td className="px-4 py-3 text-slate-400">{r.state || "—"}</td>

                                            {/* Status */}
                                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                                <StatusPill status={r.status} onChange={v => updateStatus(r, v)}/>
                                            </td>

                                            {/* Web / Email */}
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {r.website ? (
                                                        <a href={r.website} target="_blank" rel="noopener noreferrer"
                                                            title={r.website} onClick={e => e.stopPropagation()}
                                                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 max-w-[130px] truncate text-[11px]">
                                                            <Globe size={10} className="shrink-0"/>
                                                            {r.website.replace(/^https?:\/\/(www\.)?/,"").replace(/\/$/,"")}
                                                        </a>
                                                    ) : r.enriched ? (
                                                        <span className="text-slate-600 text-[11px]">No site</span>
                                                    ) : (
                                                        <span className="text-slate-700 text-[11px]">searching…</span>
                                                    )}
                                                    {r.email && (
                                                        <button title={`Email ${r.email}`}
                                                            onClick={e => { e.stopPropagation(); setEmailTarget(r); }}
                                                            className="text-green-400 hover:text-green-300 shrink-0 transition-colors">
                                                            <Mail size={11}/>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Hover action icons */}
                                            <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button title="Expand"
                                                        onClick={() => setExpandedId(prev => prev === r.id ? null : r.id)}
                                                        className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-[#374151] transition-colors">
                                                        <Eye size={12}/>
                                                    </button>
                                                    {r.website && (
                                                        <a href={r.website} target="_blank" rel="noopener noreferrer" title="Open website"
                                                            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-400 hover:bg-[#374151] transition-colors">
                                                            <Globe size={12}/>
                                                        </a>
                                                    )}
                                                    <button title="Send email via Email Center"
                                                        onClick={() => setEmailTarget(r)}
                                                        className={`p-1.5 rounded-lg transition-colors ${r.email ? "text-slate-500 hover:text-green-400 hover:bg-[#374151]" : "text-slate-700 cursor-not-allowed"}`}
                                                        disabled={!r.email}>
                                                        <Mail size={12}/>
                                                    </button>
                                                    <button title="Log contact"
                                                        onClick={() => logEmail(r, "Contacted")}
                                                        className="p-1.5 rounded-lg text-slate-500 hover:text-purple-400 hover:bg-[#374151] transition-colors">
                                                        <Send size={12}/>
                                                    </button>
                                                    <button title={r.inCRM ? "In CRM" : "Add to CRM"}
                                                        onClick={() => handleAddCRM(r)}
                                                        disabled={r.inCRM || addingCRM === r.id}
                                                        className={`p-1.5 rounded-lg transition-colors ${r.inCRM ? "text-blue-400" : "text-slate-500 hover:text-amber-400 hover:bg-[#374151]"}`}>
                                                        {addingCRM === r.id
                                                            ? <Loader size={12} className="animate-spin"/>
                                                            : r.inCRM ? <CheckCircle2 size={12}/> : <Plus size={12}/>}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* ── Expanded row ── */}
                                        {expandedId === r.id && (
                                            <ExpandedRow
                                                r={r}
                                                score={r.score}
                                                note={r.note}         onNoteChange={v => updateNote(r, v)}
                                                status={r.status}     onStatusChange={v => updateStatus(r, v)}
                                                followUp={r.followUp} onFollowUpChange={v => updateFollowUp(r, v)}
                                                emailLog={r.emailLog} onLogEmail={label => logEmail(r, label)}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500 text-sm">
                                            No results match your filter.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Table footer */}
                    {isDone && (
                        <div className="flex items-center justify-between px-5 py-3 border-t border-[#1F2937] bg-[#0D1520] text-[11px] text-slate-500">
                            <span>
                                Email hit rate: <span className="text-white font-semibold">{Math.round((stats.emails/Math.max(stats.found,1))*100)}%</span>
                                &nbsp;·&nbsp;
                                Website hit rate: <span className="text-white font-semibold">{Math.round((stats.websites/Math.max(stats.found,1))*100)}%</span>
                                &nbsp;·&nbsp;
                                Sorted by: <span className="text-white font-semibold capitalize">{sortCol}</span>
                            </span>
                            <button onClick={() => exportCSV(filtered)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 border border-green-500/30 rounded-lg text-xs text-green-400 hover:bg-green-600/30 transition-colors">
                                <Download size={11}/> Export All
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Empty state ── */}
            {!isRunning && !isDone && !error && (
                <div className="bg-[#111827] border border-[#1F2937] rounded-xl py-16 flex flex-col items-center gap-4 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <Search size={28} className="text-blue-400"/>
                    </div>
                    <div>
                        <p className="text-white font-semibold">Find Australian SME prospects</p>
                        <p className="text-slate-500 text-sm mt-1 max-w-md">
                            Search by keyword · Filter by state & industry · Score leads · Track pipeline status · Export to CSV
                        </p>
                    </div>
                    <div className="flex items-center gap-5 text-[11px] text-slate-600 mt-1 flex-wrap justify-center">
                        <span className="flex items-center gap-1.5"><Building2 size={11}/> ABR Business Register</span>
                        <span className="flex items-center gap-1.5"><Globe size={11}/> Website Discovery</span>
                        <span className="flex items-center gap-1.5"><Mail size={11}/> Email Extraction</span>
                        <span className="flex items-center gap-1.5"><Star size={11}/> Opportunity Score</span>
                        <span className="flex items-center gap-1.5"><Database size={11}/> CRM Integration</span>
                    </div>
                </div>
            )}

            {/* ── Bulk action bar (floating) ── */}
            {selected.size > 0 && (
                <BulkBar
                    count={selected.size}
                    onCRM={handleBulkCRM}
                    onExport={handleBulkExport}
                    onContacted={handleBulkContacted}
                    onClear={() => setSelected(new Set())}
                />
            )}

            {/* ── Email modal ── */}
            {emailTarget && (
                <ProspectEmailModal
                    prospect={emailTarget}
                    onClose={() => setEmailTarget(null)}
                    onSent={(tplName) => {
                        logEmail(emailTarget, `${tplName} sent`);
                        setEmailTarget(null);
                    }}
                />
            )}
        </div>
    );
}
